<template>
  <section :class="flat ? '' : 'rounded-2xl border border-main bg-surface p-6'" :data-testid="dataTestid" :aria-labelledby="headlineId">
    <header class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <h3
            :id="headlineId"
            :class="flat ? 'txt-label-m-semibold text-main' : 'txt-headline-s-bold text-main'"
            data-testid="insights-metric-card-headline"
          >
            {{ headline }}
          </h3>
          <TooltipWrapper v-if="tooltip && !hasSubheadline">
            <template #trigger>
              <button
                type="button"
                class="flex shrink-0 rounded-full text-icon-subtlest omr-focus-visible-outline hover:text-icon-main"
                :aria-label="t('Reviews.product.insights.metricCard.howToRead')"
                :aria-describedby="tooltipId"
                data-testid="insights-metric-card-tooltip-trigger"
              >
                <CircleHelpIcon class="lucide-icon-s" aria-hidden="true" />
              </button>
            </template>
            <template #content>
              <MarkdownContent :id="tooltipId" unstyled class="max-w-sm" :md="tooltip" data-testid="insights-metric-card-tooltip" />
            </template>
          </TooltipWrapper>
        </div>
        <div v-if="hasSubheadline" class="mt-1 flex items-center gap-1.5" data-testid="insights-metric-card-subheadline-row">
          <p class="txt-body-s-regular text-subtle" data-testid="insights-metric-card-subheadline">
            <slot name="subheadline">{{ subheadline }}</slot>
          </p>
          <TooltipWrapper v-if="tooltip">
            <template #trigger>
              <button
                type="button"
                class="flex shrink-0 rounded-full text-icon-subtlest omr-focus-visible-outline hover:text-icon-main"
                :aria-label="t('Reviews.product.insights.metricCard.howToRead')"
                :aria-describedby="tooltipId"
                data-testid="insights-metric-card-tooltip-trigger"
              >
                <CircleHelpIcon class="lucide-icon-s" aria-hidden="true" />
              </button>
            </template>
            <template #content>
              <MarkdownContent :id="tooltipId" unstyled class="max-w-sm" :md="tooltip" data-testid="insights-metric-card-tooltip" />
            </template>
          </TooltipWrapper>
        </div>
      </div>
      <div v-if="slots.badge" class="flex shrink-0 items-center gap-2" data-testid="insights-metric-card-badge">
        <slot name="badge" />
      </div>
    </header>

    <div class="mt-4" data-testid="insights-metric-card-content">
      <slot />
    </div>

    <footer v-if="slots.footer" class="mt-4" data-testid="insights-metric-card-footer">
      <slot name="footer" />
    </footer>
  </section>
</template>

<script setup lang="ts">
import { MarkdownContent, TooltipWrapper } from '@ramp106/omrjs-core-ui'
import { CircleHelpIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed, useId, useSlots } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  headline: string
  subheadline?: string
  tooltip?: string
  flat?: boolean
  dataTestid?: string
}>()

const { t } = useI18n()
const slots = useSlots()
const headlineId = useId()
const tooltipId = useId()
const hasSubheadline = computed(() => !!props.subheadline || !!slots.subheadline)
</script>
